import { useState, useCallback, useEffect } from "react";
import { getFriendlyErrorMessage } from "../utils/errorMapper";
import { useAuth } from "@/context/AuthContext";

const API_BASE = "https://wpp.debugin.com/ai";

export interface PlanInfo {
  name: string;
  features: {
    messages: number;
    tokens: number;
    input: number;
    output: number;
  };
}

export interface UsageInfo {
  messages: number;
  tokens: number;
  input: number;
  output: number;
}

export interface AutomationSettings {
  status: boolean;
  updated_at: string;
  reply: {
    voice: boolean;
    image: boolean;
    document: boolean;
  };
}

export interface CsvRow {
  [key: string]: string;
}

export interface ContactAiInfo {
  label?: string;
  label_name?: string;
  reason?: string;
  source?: string;
  wpp_label_id?: string;
  updated_at?: string;
}

export interface DashboardData {
  session: string;
  phone: string;
  plan: PlanInfo;
  usage: UsageInfo;
  automation: AutomationSettings;
  blacklist: string[];
  csvData: CsvRow[];
  contacts?: Record<string, ContactAiInfo>;
}

interface ManageResponse {
  success: boolean;
  message: string;
  data: {
    session: string;
    token: string;
    phone: string;
    automation: AutomationSettings;
    plan: PlanInfo;
    usage: UsageInfo;
    blacklist: string[];
    csvData: CsvRow[];
    contacts?: Record<string, ContactAiInfo>;
  };
}

interface UpdateResponse {
  success: boolean;
  message: string;
  data: {
    status?: boolean;
    reply?: { voice: boolean; image: boolean; document: boolean };
    blacklist?: string[];
    rows?: number;
  };
}

interface ImportResponse {
  success: boolean;
  message: string;
  data: {
    rows: number;
    plan: string;
    expires_at: string;
  };
}

export function useDashboardData() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState<string | null>(null);
  const setError = useCallback((err: string | null) => {
    setErrorState(err ? getFriendlyErrorMessage(err) : null);
  }, []);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const session = user?.session;
  const token = user?.token;

  const fetchData = useCallback(async () => {
    if (!session || !token) {
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/manage/${session}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json: ManageResponse = await res.json();
      if (json.success) {
        setData({
          session: json.data.session,
          phone: json.data.phone,
          plan: json.data.plan,
          usage: json.data.usage,
          automation: json.data.automation,
          blacklist: json.data.blacklist || [],
          csvData: json.data.csvData || [],
          contacts: json.data.contacts || {},
        });
      } else {
        setError((json as any).error || "Failed to load data");
      }
    } catch (err) {
      console.error("[useDashboardData] Fetch error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [session, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateAutomation = useCallback(
    async (fields: Record<string, string>): Promise<boolean> => {
      if (!session || !token) return false;
      setActionLoading("automation");
      try {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        const res = await fetch(`${API_BASE}/manage/${session}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const json: UpdateResponse = await res.json();
        if (json.success) {
          setData((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, automation: { ...prev.automation, updated_at: new Date().toISOString() } };
            if ("status" in fields) {
              updated.automation.status = fields.status === "true";
            }
            const reply = { ...updated.automation.reply };
            if ("reply_voice" in fields) reply.voice = fields.reply_voice === "true";
            if ("reply_image" in fields) reply.image = fields.reply_image === "true";
            if ("reply_document" in fields) reply.document = fields.reply_document === "true";
            updated.automation.reply = reply;
            return updated;
          });
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [session, token]
  );

  const toggleAutomationStatus = useCallback(
    async (on: boolean): Promise<boolean> => {
      return updateAutomation({ status: on ? "true" : "false" });
    },
    [updateAutomation]
  );

  const updateReplySettings = useCallback(
    async (settings: { voice: boolean; image: boolean; document: boolean }): Promise<boolean> => {
      return updateAutomation({
        reply_voice: settings.voice ? "true" : "false",
        reply_image: settings.image ? "true" : "false",
        reply_document: settings.document ? "true" : "false",
      });
    },
    [updateAutomation]
  );

  const addToBlacklist = useCallback(
    async (number: string): Promise<boolean> => {
      if (!session || !token) return false;
      setActionLoading("blacklist");
      try {
        const formData = new FormData();
        formData.append("blacklist_add", number);
        const res = await fetch(`${API_BASE}/manage/${session}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const json: UpdateResponse = await res.json();
        if (json.success) {
          await fetchData();
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [session, token, fetchData]
  );

  const removeFromBlacklist = useCallback(
    async (number: string): Promise<boolean> => {
      if (!session || !token) return false;
      setActionLoading("blacklist");
      try {
        const formData = new FormData();
        formData.append("blacklist_remove", number);
        const res = await fetch(`${API_BASE}/manage/${session}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const json: UpdateResponse = await res.json();
        if (json.success) {
          await fetchData();
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [session, token, fetchData]
  );

  const importCsv = useCallback(
    async (file: File, phone: string): Promise<{ success: boolean; message?: string; data?: { rows: number; plan: string; expires_at: string } }> => {
      if (!session || !token) return { success: false, message: "No active session" };
      setActionLoading("import");
      try {
        const formData = new FormData();
        formData.append("csv", file);
        const res = await fetch(`${API_BASE}/manage/${session}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const json: ImportResponse = await res.json();
        if (json.success) {
          await fetchData();
          return { success: true, data: json.data };
        }
        return { success: false, message: (json as any).error || "Import failed" };
      } catch {
        return { success: false, message: "Network error. Please try again." };
      } finally {
        setActionLoading(null);
      }
    },
    [session, token, fetchData]
  );

  return {
    data,
    loading,
    error,
    actionLoading,
    fetchData,
    toggleAutomationStatus,
    updateReplySettings,
    addToBlacklist,
    removeFromBlacklist,
    importCsv,
  };
}